import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, ChevronRight, Link2, Network, Shuffle } from "lucide-react";
import {
  CUSTOMER_TYPE_LABELS,
  NODE_STATUS_LABELS,
  buildReferralLink,
  formatReferralCode,
  type AdminNetworkItem,
  type BranchRecalc,
} from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Textarea, type BadgeVariant } from "@/shared/ui/core";
import { EmptyState, Modal, SkeletonCard } from "@/shared/ui/ui";
import { cn } from "@/shared/lib/cn";
import { AdminGuard } from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";
import { listAdminNetwork, reassignSponsor } from "./network.api";

const TYPE_VARIANTS: Record<AdminNetworkItem["customerType"], BadgeVariant> = {
  retail: "neutral",
  preferred_customer: "info",
  distributor: "indigo",
};

const STATUS_VARIANTS: Record<AdminNetworkItem["status"], BadgeVariant> = {
  active: "success",
  inactive: "neutral",
  suspended: "error",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Red · Unilevel — el árbol EXPANDIBLE de la referencia (Anahí, 27-ago),
 * mejorado: cada raíz es una card; dentro, cada persona es una fila con
 * su avatar de iniciales, "tipo · nivel · directos", su código con el
 * enlace copiable, su estado y **Reasignar** — que abre el modal de
 * mover la rama, con motivo OBLIGATORIO (mover el árbol mueve el dinero
 * de todos, y el porqué queda registrado).
 *
 * El chevron pliega ramas enteras: una red grande se lee por partes.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminNetworkPage() {
  return (
    <AdminGuard>
      <NetworkScreen />
    </AdminGuard>
  );
}

interface TreeNode extends AdminNetworkItem {
  children: TreeNode[];
}

/** El árbol desde la lista plana. Hijos por fecha de alta. */
function buildTree(items: AdminNetworkItem[]): TreeNode[] {
  const nodes = new Map<string, TreeNode>(
    items.map((item) => [item.userId, { ...item, children: [] }]),
  );
  const roots: TreeNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.sponsorId ? nodes.get(node.sponsorId) : undefined;
    // Un patrocinador que no está en la lista no debería pasar: si pasa,
    // la rama se ENSEÑA como raíz en vez de esconderse.
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

function NetworkScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const network = useQuery({ queryKey: ["admin", "network"], queryFn: listAdminNetwork });

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState<AdminNetworkItem | null>(null);

  const items = network.data ?? [];
  const roots = useMemo(() => buildTree(items), [items]);
  const totals = useMemo(
    () => ({
      total: items.length,
      distributors: items.filter((item) => item.customerType === "distributor").length,
      clients: items.filter((item) => item.customerType !== "distributor").length,
    }),
    [items],
  );

  function toggle(userId: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function copiarEnlace(item: AdminNetworkItem) {
    try {
      await navigator.clipboard.writeText(
        buildReferralLink(item.referralCode, window.location.origin),
      );
      setCopied(item.userId);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Sin permiso de portapapeles: el código sigue visible en la fila.
    }
  }

  function initialsOf(name: string): string {
    const words = name.trim().split(/\s+/);
    return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase() || "?";
  }

  function NodeRow({ node, indent }: { node: TreeNode; indent: number }) {
    const isCollapsed = collapsed.has(node.userId);
    return (
      <>
        <div
          className={cn(
            "flex flex-wrap items-center gap-3 rounded-2xl px-3 py-2.5 transition hover:bg-ds-mist/40",
            indent > 0 && "border-l-2 border-ds-border/60",
          )}
          style={indent > 0 ? { marginLeft: `${Math.min(indent, 8) * 24}px` } : undefined}
        >
          {/* El chevron SOLO si hay rama que plegar. */}
          {node.children.length > 0 ? (
            <button
              type="button"
              onClick={() => toggle(node.userId)}
              aria-label={isCollapsed ? t("Desplegar la rama") : t("Plegar la rama")}
              aria-expanded={!isCollapsed}
              className="rounded-full p-1 text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-ink"
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="w-6" />
          )}

          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ds-mist text-[12px] font-bold text-ds-violet-pop">
            {initialsOf(node.displayName)}
          </span>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-semibold text-ds-ink">{node.displayName}</p>
            <p className="truncate text-[12px] text-ds-ink-3">
              {t(CUSTOMER_TYPE_LABELS[node.customerType])} ·{" "}
              {node.depth === 0 ? t("Raíz") : t("Nivel {{n}}", { n: node.depth })}
              {node.directCount > 0 &&
                ` · ${t("{{n}} directo(s)", { n: node.directCount })}`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void copiarEnlace(node)}
              className="inline-flex items-center gap-1.5 rounded-ds-pill border border-ds-border bg-white px-2.5 py-1 font-mono text-[12px] text-ds-ink-3 transition hover:border-ds-violet-pop hover:text-ds-violet-pop"
              aria-label={t("Copiar enlace de invitación")}
            >
              {formatReferralCode(node.referralCode)}
              {copied === node.userId ? (
                <Check className="h-3.5 w-3.5 text-ds-violet-pop" />
              ) : (
                <Link2 className="h-3.5 w-3.5" />
              )}
            </button>
            <Badge variant={TYPE_VARIANTS[node.customerType]} size="sm">
              {t(CUSTOMER_TYPE_LABELS[node.customerType])}
            </Badge>
            <Badge variant={STATUS_VARIANTS[node.status]} size="sm" dot>
              {t(NODE_STATUS_LABELS[node.status])}
            </Badge>
            <span className="hidden text-[12px] text-ds-ink-3 sm:inline">
              {formatOrderDate(node.enrolledAt)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Shuffle />}
              onClick={() => setReassigning(node)}
            >
              {t("Reasignar")}
            </Button>
          </div>
        </div>

        {!isCollapsed &&
          node.children.map((child) => (
            <NodeRow key={child.userId} node={child} indent={indent + 1} />
          ))}
      </>
    );
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Red")}
        description={t("El unilevel completo: cada quien debajo de quien lo invitó.")}
      />

      {items.length > 0 && (
        <p className="mb-4 text-[13px] text-ds-ink-3">
          {t("{{total}} en la red · {{d}} socios · {{c}} clientes", {
            total: totals.total,
            d: totals.distributors,
            c: totals.clients,
          })}
        </p>
      )}

      {network.isPending ? (
        <SkeletonCard />
      ) : roots.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-ds-border p-4">
          <EmptyState
            icon={<Network className="h-5 w-5 text-ds-ink-3" />}
            title={t("La red está vacía")}
            description={t("Cuando alguien comparta su enlace y registren con él, el árbol crece aquí.")}
          />
        </div>
      ) : (
        <div className="grid gap-4">
          {roots.map((root) => (
            <div
              key={root.userId}
              className="rounded-3xl border border-ds-border bg-white p-2 shadow-ds-float sm:p-3"
            >
              <NodeRow node={root} indent={0} />
            </div>
          ))}
        </div>
      )}

      <ReassignModal
        node={reassigning}
        onClose={() => setReassigning(null)}
        onDone={() => {
          setReassigning(null);
          void queryClient.invalidateQueries({ queryKey: ["admin", "network"] });
        }}
      />
    </>
  );
}

/**
 * Mover la rama: el nuevo patrocinador POR SU CÓDIGO (vacío = a la
 * raíz) y el motivo obligatorio. El servidor impide ciclos y valida que
 * el código exista — aquí solo se pregunta bien.
 */
function ReassignModal({
  node,
  onClose,
  onDone,
}: {
  node: AdminNetworkItem | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<BranchRecalc | null | undefined>(undefined);

  const mover = useMutation({
    mutationFn: () =>
      reassignSponsor(node?.userId ?? "", code.trim() ? code.trim() : null, reason.trim()),
    onSuccess: (result) => {
      setCode("");
      setReason("");
      // Se enseña qué pasó con las comisiones antes de cerrar (14-sep).
      setDone(result.recalculated);
      onDone();
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo reasignar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!reason.trim()) {
      setError(t("Escribe por qué se reasigna: queda registrado"));
      return;
    }
    mover.mutate();
  }

  return (
    <Modal
      open={node !== null}
      onClose={onClose}
      title={node ? t("Reasignar a {{name}}", { name: node.displayName }) : ""}
    >
      <form onSubmit={enviar} className="grid gap-4">
        {/*
          Se acepta el ENLACE de invitación completo o el código (Anahí,
          14-sep): lo que se copia de la fila es el enlace, y aquí se
          pega tal cual. El servidor saca el código del último tramo.
        */}
        <Input
          label={t("Enlace o código del nuevo patrocinador")}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder={`${window.location.origin}/register/1111-1134`}
          hint={t("Pega el enlace de invitación (o solo el código). Déjalo vacío para colgar la rama de la raíz.")}
        />
        <Alert variant="info">
          {t("Al mover la rama, las comisiones PENDIENTES que generaron sus compras se borran y se vuelven a calcular con la nueva línea. Lo ya pagado no se toca.")}
        </Alert>
        {done !== undefined && (
          <Alert variant="success" onDismiss={() => setDone(undefined)}>
            {done
              ? t("Rama movida. Comisiones recalculadas: {{removed}} pendientes retiradas, {{inserted}} nuevas de inicio rápido{{cuts}}.", {
                  removed: done.removed,
                  inserted: done.fastStartInserted,
                  cuts: done.cutsRerun.length ? `, ${t("cortes de red repetidos")}: ${done.cutsRerun.join(", ")}` : "",
                })
              : t("Rama movida, pero las comisiones no se pudieron recalcular: repite la reasignación o avísame.")}
          </Alert>
        )}
        <Textarea
          label={t("Motivo")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          placeholder={t("Por qué se mueve esta rama")}
        />
        {error && (
          <Alert variant="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" type="button" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button type="submit" loading={mover.isPending}>
            {t("Mover la rama")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
